import { displayText, displayField, displayOptions, countryName } from "../locales/display";
import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button, { TButtonVariant } from "@ui/button/Button";
import { Checkbox } from "@ui/checkbox/Checkbox";
import { Error } from "@ui/error/Error";
import { FileSelect } from "@ui/fileSelect/FileSelect";
import { IconClose } from "@ui/icons/IconClose";
import { IconCopy } from "@ui/icons/IconCopy";
import Input from "@ui/input/Input";
import { Link } from "@ui/link/Link";
import { Radio } from "@ui/radio/Radio";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";
import { ITheme, RecursiveCSS } from "@ui/theme/GlobalTheme";
import { CountriesData } from "@ui/utils/CountriesData";

import { createContext, Fragment, useContext, useEffect, useId, useRef, useState } from "react";
import { FormDirtyContext } from "./FormDirtyContext";
import { createEmptyFormItem } from "./formArrayItem";
import styled, { useTheme } from "styled-components";
import { fieldActionDependency, fieldActionVisible, parseFieldActions, type FormFieldActionEvent } from "./formFieldActions";
import { notify } from "../tools/notify";
import { randomSHA256 } from "../tools/tools";
import { HLine } from "./HLine";
import { DatePickerField } from "./conponents/DateRangePicker";
import { S_CONFIRM } from "./popup/general/ConfirmDialog";
import { S_SELECT_COUNTRIES_POPUP } from "./popup/SelectCountriesPopup";
import { S_SELECT_ITEMS_POPUP } from "./popup/SelectItemsPopup";

export interface TFormButton {
	title: string;
	disabled?: boolean;
	onClick?: (data?: any) => void;
	sx?: RecursiveCSS;
	variant?: TButtonVariant;
}

export type FormParserSize = "small" | "medium" | "large";

export type FormParserAppearance = "classic" | "modern";
const FormAppearanceContext = createContext<FormParserAppearance>("classic");

const RemoveItemButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 32px;
	width: 32px;
	height: 32px;
	padding: 0;
	margin-left: auto;
	border: 1px solid #cdd5e2;
	border-radius: 8px;
	background: #fff;
	color: #667085;
	cursor: pointer;
	transition: background .15s, border-color .15s, color .15s;
	svg { width: 16px; height: 16px; }
	&:hover { background: #fff1f3; border-color: #fda4af; color: #be123c; }
	&:focus-visible { outline: 2px solid #6374d9; outline-offset: 3px; }
`;

type TFormParserVariant = "default" | "reference";
type TFieldErrors = Record<string, string[]>;

const getReferenceFormButtonSx = (variant?: TFormParserVariant, modern = false): RecursiveCSS => {
	if (modern) return { minHeight: "34px", borderRadius: "8px", boxShadow: "none", fontSize: "13px", fontWeight: 500 };
	if (variant !== "reference") return {};

	return {
		height: "30px",
		padding: "0 12px",
		borderRadius: "7px",
		boxShadow: "none",
		fontSize: "11px",
		fontWeight: 500,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
	};
};

const normalizeFieldPath = (parts: string[]): string => {
	return parts
		.filter(part => part !== "")
		.map(part => part.replace(/\[(\d+)\]/g, ".$1"))
		.join(".");
};

const getItemPath = (parentPath: string[], item: IBlockItem): string[] => {
	if (item.name && item.name.startsWith("$link_")) return [...parentPath, String(item.content)];
	if (item.linked && item.content && typeof item.content === "object" && !Array.isArray(item.content)) {
		const keys = Object.keys(item.content);
		if (keys.length > 0 && keys.every(key => key.startsWith("$link_"))) return parentPath;
	}
	return [...parentPath, String(item.name)];
};

const parseErrorDetailField = (detail: unknown): { path: string; message: string } | null => {
	const text = String(detail);
	const match = text.match(/["']([^"']+)["']/);
	if (!match) return null;

	const rawPath = match[1];
	const parts = rawPath
		.replace(/\[(\d+)\]/g, ".$1")
		.split(".")
		.filter(Boolean);
	if (parts[0] === "data") parts.shift();
	if (parts.length === 0) return null;

	const message =
		text
			.replace(match[0], "")
			.replace(/\s*for field\s*/i, "")
			.replace(/\s+/g, " ")
			.trim() || text;

	return {
		path: normalizeFieldPath(parts),
		message,
	};
};

const getFormDataRoot = (formData: any) => {
	if (formData && typeof formData === "object" && formData.data && typeof formData.data === "object") return formData.data;
	return formData;
};

const hasFieldPath = (formData: any, path: string): boolean => {
	const root = getFormDataRoot(formData);
	if (!root || typeof root !== "object") return false;

	const parts = path.split(".").filter(Boolean);
	let current = root;

	for (let i = 0; i < parts.length; i++) {
		const part = parts[i];
		if (current === null || current === undefined) return false;
		if (Array.isArray(current)) {
			const index = Number(part);
			if (!Number.isInteger(index) || index < 0 || index >= current.length) return false;
			current = current[index];
			continue;
		}
		if (typeof current !== "object" || !(part in current)) return false;
		current = current[part];
	}

	return true;
};

const parseSubmitError = (submitError: any, formData: any): { fieldErrors: TFieldErrors; topError: any | null } => {
	if (!submitError || !Array.isArray(submitError.details)) {
		return { fieldErrors: {}, topError: submitError ?? null };
	}

	const fieldErrors: TFieldErrors = {};
	const remainingDetails: unknown[] = [];

	for (const detail of submitError.details) {
		const parsed = parseErrorDetailField(detail);
		if (!parsed || !hasFieldPath(formData, parsed.path)) {
			remainingDetails.push(detail);
			continue;
		}

		if (!fieldErrors[parsed.path]) fieldErrors[parsed.path] = [];
		fieldErrors[parsed.path].push(parsed.message);
	}

	const topError = remainingDetails.length > 0 ? { ...submitError, details: remainingDetails } : null;
	return { fieldErrors, topError };
};

const formatFieldErrorMessage = (message: string): string => {
	if (!message) return "";
	const trimmed = message.trim();
	return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const TOP_FORM_ERROR_ID = "form_parser_top_error";

const getFieldErrorId = (path: string): string => {
	return "form_parser_field_error_" + path.replace(/[^a-zA-Z0-9_-]/g, "_");
};

const scrollToFormError = (fieldErrors: TFieldErrors, topError: any | null): void => {
	const firstFieldPath = Object.keys(fieldErrors)[0];
	const targetID = firstFieldPath ? getFieldErrorId(firstFieldPath) : topError ? TOP_FORM_ERROR_ID : null;
	if (!targetID) return;

	window.setTimeout(() => {
		document.getElementById(targetID)?.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});
	}, 0);
};

// Form data is mutated in place, like the Input fields do; local state only re-renders the picker.
const FormDateField = ({ container, name, onChange, ...props }: {
	container: Record<string, any>; name: string; id: string; label?: string; size: "small" | "medium"; disabled?: boolean; invalid: boolean; onChange: () => void;
}) => {
	useLocale();
	const stored = container[name];
	const normalized = typeof stored === "string" && /^\d{4}-\d{2}-\d{2}/.test(stored) ? stored.slice(0, 10) : "";
	const [value, setValue] = useState(normalized);
	useEffect(() => setValue(normalized), [normalized]);
	return <DatePickerField {...props} value={value} allowClear onChange={next => {
		container[name] = next;
		setValue(next);
		onChange();
	}} />;
};

const fieldErrorControlSx: RecursiveCSS = {
	border: "1px solid #dc2626",
	boxShadow: "0 0 0 2px rgba(220,38,38,.12)",
	"&:hover": {
		border: "1px solid #dc2626",
	},
};

const getInputFieldErrorSx = (id: string): RecursiveCSS => ({
	["#control_" + id]: fieldErrorControlSx,
});

interface IFormParser<T = unknown> {
	/** Opt-in card layout; classic preserves existing forms. */
	appearance?: FormParserAppearance;
	onSubmit: (data: T) => Promise<any>;
	onComplete?: () => void;
	onRequest: () => Promise<T>;
	onChange?: (data: T) => void;
	requestFormUpdater?: (updater: () => void) => void;
	ignoreObserver?: boolean;
	id?: string;
	secondaryButton?: TFormButton | null;
	primaryButton?: TFormButton | null;
	onComponentClick?: (item: IBlockItem) => void;
	onFieldAction?: (event: FormFieldActionEvent) => void;
	sx?: RecursiveCSS;
	fullSizeButtons?: boolean;
	scrollable?: boolean;
	buttons?: TFormButton[];
	grow?: boolean;
	size?: FormParserSize;
	variant?: TFormParserVariant;
	previewMode?: boolean;
}

export const FormParser = <T,>({
	sx,
	grow,
	onComponentClick,
	onFieldAction,
	ignoreObserver,
	secondaryButton,
	primaryButton,
	fullSizeButtons,
	scrollable,
	onSubmit,
	onRequest,
	onComplete,
	onChange,
	buttons,
	size,
	variant = "default",
	appearance = "classic",

	requestFormUpdater,
	previewMode,
}: IFormParser<T>) => {
	useLocale();
	const error = useState<any | null>(null);
	const data = useState<any | null>(null);
	const busy = useState<boolean>(false);
	const submitting = useRef(false);
	const mounted = useRef(true);
	const operationVersion = useRef(0);
	const [fieldErrors, setFieldErrors] = useState<TFieldErrors>({});
	const [topError, setTopError] = useState<any | null>(null);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const reportDirty = useContext(FormDirtyContext);
	const formId = useId();
	useEffect(() => {
		reportDirty?.(formId, unsavedChanges);
		return () => reportDirty?.(formId, false);
	}, [reportDirty, formId, unsavedChanges]);
	const [variableWatcher, _] = useState<string[]>([]);
	const [_r, setRerender] = useState<number>(0);
	//const initialDataRef = useRef<string>("");
	if (!size) size = "medium";

	useEffect(() => {
		mounted.current = true;
		sendRequest();
		if (requestFormUpdater) {
			requestFormUpdater(() => {
				setRerender(Date.now());
			});
		}
		return () => {
			mounted.current = false;
			operationVersion.current++;
			submitting.current = false;
		};
	}, []);

	const sendRequest = async () => {
		if (unsavedChanges) {
			S_CONFIRM.invoke({
				message: msg("confirm_5bf69a50"),
				onResult: (val: "confirm" | "reject") => {
					if (val === "confirm") {
						reloadData();
					}
				},
			});
		} else {
			reloadData();
		}
	};

	const reloadData = async () => {
		if (!mounted.current || submitting.current) return;
		const operation = ++operationVersion.current;
		const isCurrent = () => mounted.current && operationVersion.current === operation;
		setUnsavedChanges(false);
		busy[1](true);
		error[1](null);
		setTopError(null);
		setFieldErrors({});
		try {
			const result = await onRequest();
			if (isCurrent()) setData(result);
		} catch (requestError) {
			if (!isCurrent()) return;
			const nextError: IError = { code: -1, message: requestError instanceof globalThis.Error ? requestError.message : msg("requestFailed") };
			error[1](nextError);
			setTopError(nextError);
		} finally {
			if (isCurrent()) busy[1](false);
		}
	};

	const setData = (result: any, scrollToError = false) => {
		if (result && typeof result === "object") {
			let err = false;

			if ("error" in result && typeof result.error === "object" && "code" in result.error) {
				err = true;
				error[1](result.error);
				applySubmitError(result.error, scrollToError);
			}

			if (!err && "code" in result && "message" in result) {
				err = true;
				error[1](result);
				applySubmitError(result, scrollToError);
			}

			if (!err && result) {
				error[1](null);
				setTopError(null);
				setFieldErrors({});
				setUnsavedChanges(false);
				let isApiResponse = false;
				if (result.data && result.data.data) isApiResponse = true;
				if (isApiResponse) {
					data[1](result.data);
				} else {
					if ("data" in result) data[1](result.data);
					else data[1](result);
				}

				/*
				const rawData = result.data ?? result;
				data[1](rawData);*/
			}
		} else {
			error[1]({ code: -1, message: msg("wrongData") });
			setTopError({ code: -1, message: msg("wrongData") });
			if (scrollToError) scrollToFormError({}, { code: -1, message: msg("wrongData") });
		}
	};

	const applySubmitError = (submitError: any, scrollToError = false) => {
		const parsed = parseSubmitError(submitError, data[0]);
		setFieldErrors(parsed.fieldErrors);
		setTopError(parsed.topError);
		if (scrollToError) scrollToFormError(parsed.fieldErrors, parsed.topError);
	};

	const onFieldChange = (fieldName?: string, fieldPath?: string) => {
		setUnsavedChanges(true);
		if (fieldPath) {
			setFieldErrors(prev => {
				const pathsToClear = Object.keys(prev).filter(path => path === fieldPath || path.startsWith(fieldPath + "."));
				if (pathsToClear.length === 0) return prev;
				const next = { ...prev };
				for (const path of pathsToClear) delete next[path];
				return next;
			});
		}
		if (fieldName) {
			for (const i of variableWatcher) {
				if (fieldName === i) {
					setRerender(Date.now());
					break;
				}
			}
		}
		if (onChange) onChange(data[0]);
	};

	const sendSubmit = async (_e?: any) => {
		if (previewMode || busy[0] || submitting.current || !mounted.current || !data[0]) return;
		submitting.current = true;
		const operation = ++operationVersion.current;
		const isCurrent = () => mounted.current && operationVersion.current === operation;
		busy[1](true);
		error[1](null);
		setTopError(null);
		setFieldErrors({});

		try {
			const result = await onSubmit(data[0]);
			if (!isCurrent()) return;
			setData(result, true);
			onComplete && !result?.error && onComplete();
			if (result && result.error) error[1](result.error);
		} catch (submitError) {
			if (!isCurrent()) return;
			const nextError: IError = {
				code: -1,
				message: submitError instanceof globalThis.Error ? submitError.message : msg("submitFailed"),
			};
			error[1](nextError);
			setTopError(nextError);
		} finally {
			if (isCurrent()) {
				submitting.current = false;
				busy[1](false);
			}
		}
	};

	let content = null;

	const renderButtons = [...(buttons ?? [])];

	if (secondaryButton !== null && !previewMode) {
		renderButtons.push({
			title: secondaryButton?.title ?? msg("restore"),
			onClick:
				secondaryButton?.onClick ??
				(() => {
					sendRequest();
				}),
			variant: secondaryButton?.variant ?? ("secondarySmall" as TButtonVariant),
			sx: secondaryButton?.sx,
		});
	}

	if (primaryButton !== null && !previewMode) {
		let primaryTitle = primaryButton?.title ?? msg("save");
		let variant: TButtonVariant = primaryButton?.variant ?? ("primarySmall" as TButtonVariant);

		if (!ignoreObserver) {
			if (!unsavedChanges) {
				variant = "secondarySmall";
			} else {
				primaryTitle = primaryButton?.title ?? msg("saveUnsavedChanges");
			}
		}

		renderButtons.push({
			title: primaryTitle,
			disabled: primaryButton?.disabled || !data[0],
			onClick:
				primaryButton?.onClick ??
				((e?: any) => {
					if (!onSubmit) return;
					sendSubmit(e);
				}),
			variant: variant,
			sx: primaryButton?.sx,
		});
	}

	const buttonsElement = renderButtons.map((button, index) => (
		<Button
			key={index}
			disabled={busy[0] || button.disabled}
			sx={{ ...getReferenceFormButtonSx(variant, appearance === "modern"), ...button.sx }}
			variant={button.variant ?? ("primarySmall" as TButtonVariant)}
			onClick={(e?: any) => {
				if (busy[0] || submitting.current || button.disabled) return;
				if (e && e.shiftKey) {
					e.preventDefault();
					e.stopPropagation();
					return;
				}

				if (button.onClick) {
					button.onClick(data[0]);
				}
			}}
			title={button.title ?? msg("untitledButton")}
		/>
	));

	const registerLinkedVariable = (name: string) => {
		if (variableWatcher.indexOf(name) !== -1) return;
		variableWatcher.push(name);
	};

	if (data[0]) {
		content = (
			<>
				<Block
					fieldErrors={fieldErrors}
					previewMode={previewMode}
					variant={variant}
					size={size}
					parentIndex="_root_"
					path={[]}
					registerLinkedVariable={registerLinkedVariable}
					onComponentClick={onComponentClick}
					onFieldAction={onFieldAction}
					data={data[0].data ?? data[0]}
					parent={null}
					onFieldChange={onFieldChange}
				/>
			</>
		);
	}

	if (scrollable) {
		if (!sx) sx = {};
		sx = {
			...sx,
			height: "100%",
			overflowY: "auto",
			flexGrow: 1,
		};
	}

	return (
		<FormAppearanceContext.Provider value={appearance}>
			{scrollable && topError && (
				<Box id={TOP_FORM_ERROR_ID}>
					<Error error={topError} />
					<Spacer size="20px" />
				</Box>
			)}

			<Box
				style={{
					maxWidth: grow ? "auto" : "800px",
					...(appearance === "modern" ? { backgroundColor: "#f5f7fa", padding: "20px", borderRadius: "12px", boxSizing: "border-box" as const } : {}),
					...sx,
				}}
				sx={{
					width: "100%",
					position: "relative",
					display: "flex",
					flexDirection: "column",
				}}>
				{!scrollable && (
					<>
						{topError && (
							<Box id={TOP_FORM_ERROR_ID}>
								<Error error={topError} />
								<Spacer size="20px" />
							</Box>
						)}
					</>
				)}
				{scrollable && (
					<Box
						sx={{
							position: "absolute",
							width: appearance === "modern" ? "calc(100% - 40px)" : "100%",
							padding: "0px 3px 3px 0px",
						}}>
						{content}
					</Box>
				)}
				{!scrollable && (
					<>
						{content}
						{!previewMode && buttonsElement.length > 0 && (
							<>
								<Spacer size="20px" />
								<HBox gap="10px">
									{!fullSizeButtons && <Spacer grow />}
									{buttonsElement}
								</HBox>
							</>
						)}
					</>
				)}
			</Box>
			{scrollable && !previewMode && buttonsElement.length > 0 && (
				<>
					<Spacer size="20px" />
					<HBox gap="10px">
						{!fullSizeButtons && <Spacer grow />}
						{buttonsElement}
					</HBox>
				</>
			)}
		</FormAppearanceContext.Provider>
	);
};

const parseBlickItems = (data: any, isParentLinked: boolean | undefined, parent: any, registerLinkedVariable: (name: string) => any): IBlockItem[] => {
	const items: IBlockItem[] = [];

	if (data && typeof data === "object") {
		for (const i in data) {
			if (i.startsWith("$__")) continue;

			let options: Record<string, string> | null = null;

			// find options
			if (isParentLinked) {
				const pname = data[i];
				for (const j in parent) {
					if (j.startsWith("$__" + pname + "_")) {
						const oname = j.replace("$__" + pname + "_", "");
						if (!options) options = {};
						if (oname) options[oname] = parent[j];
					}
					if (j === "$__" + pname) {
						if (!options) options = {};
						options = { ...options, ...parent[j] };
					}
				}
			} else {
				for (const j in data) {
					if (j.startsWith("$__" + i + "_")) {
						const oname = j.replace("$__" + i + "_", "");
						if (!options) options = {};
						if (oname) options[oname] = data[j];
					}
					if (j === "$__" + i) {
						if (!options) options = {};
						options = { ...options, ...data[j] };
					}
				}
			}

			if (options?.decorator) {
				const decorator = options.decorator as unknown as Record<string, any>;
				if (decorator.gridTemplateColumns) options.gridColumns = decorator.gridTemplateColumns;
				if (decorator.sx) options.sx = decorator.sx;

				if (decorator.blockless) options.blockless = decorator.blockless;
			}

			items.push({
				name: i,
				content: data[i],
				options: options,
				container: isParentLinked ? parent : data,
				registerLinkedVariable: registerLinkedVariable,
			});
		}
	} else if (data && typeof data !== "object") {
		if (Array.isArray(parent)) {
			const index = parent.findIndex((val: any) => val === data);
			if (index > -1 && data) {
				items.push({
					name: index + "",
					content: data,
					options: { label: null, type: "plain" },
					container: parent,
					registerLinkedVariable: registerLinkedVariable,
				});
			}
		} else {
			console.warn("Wrong block content", data);
		}
	}

	return items;
};

interface IBlockProps {
	data: any;
	parent: any;
	path: string[];
	fieldErrors?: TFieldErrors;
	parentIndex?: string;
	parentOptions?: Record<string, string | null> | null;
	isParentLinked?: boolean;
	previewMode?: boolean;
	onComponentClick?: (component: IBlockItem) => void;
	onFieldAction?: (event: FormFieldActionEvent) => void;
	registerLinkedVariable: (name: string) => any;
	size: "small" | "medium" | "large";
	variant?: TFormParserVariant;
	possibleValues?: any[];
	onFieldChange?: (fieldName?: string, fieldPath?: string) => void;
}
interface IBlockItem {
	name: string;
	content: any;
	options: Record<string, string | null> | null;
	container: any;
	linked?: boolean;
	registerLinkedVariable: (name: string) => any;
}

export const Block = (props: IBlockProps) => {
	useLocale();
	const modern = useContext(FormAppearanceContext) === "modern";
	const [state, setState] = useState<number>(0);

	let items: IBlockItem[] = parseBlickItems(props.data, props.isParentLinked, props.parent, props.registerLinkedVariable);

	let decorator = null;

	// decorator for upper level
	if (props.parent === null && props.data && typeof props.data === "object" && "$__decorator" in props.data)
		decorator = props.data["$__decorator"] as unknown as Record<string, any>;

	// decorator from parentOptions
	if (props.parentOptions && props.parentOptions.decorator) decorator = props.parentOptions.decorator as unknown as Record<string, any>;

	if (decorator) {
		if (decorator.order && Array.isArray(decorator.order)) {
			items = items.filter(val => decorator.order.indexOf(val.name) !== -1);
			items.sort((a, b) => {
				const i1 = decorator.order.indexOf(a.name);
				const i2 = decorator.order.indexOf(b.name);
				if (i1 === -1 && i2 === -1) return 0;
				if (i1 === -1) return 1;
				if (i2 === -1) return -1;
				return i1 - i2;
			});
		} else if (decorator.blocks) {
			const blockItems: IBlockItem[] = [];

			for (const block of decorator.blocks) {
				let workingItems = [...items];

				if (block.order && Array.isArray(block.order)) workingItems = workingItems.filter(val => block.order.indexOf(val.name) !== -1);

				// sort
				if (block.order && Array.isArray(block.order)) {
					workingItems.sort((a, b) => {
						const i1 = block.order.indexOf(a.name);
						const i2 = block.order.indexOf(b.name);
						if (i1 === -1 && i2 === -1) return 0;
						if (i1 === -1) return 1;
						if (i2 === -1) return -1;
						return i1 - i2;
					});
				}

				const opt: Record<string, string> = {};
				if (block.gridTemplateColumns) opt.gridColumns = block.gridTemplateColumns;
				if (block.sx) opt.sx = block.sx;
				if (block.blockless) opt.blockless = block.blockless;

				const content: Record<string, string> = {};
				for (const i in workingItems) {
					const itm = workingItems[i];
					content["$link_" + i] = itm.name;
				}

				if (block.blockless !== undefined && block.blockless !== null) opt.blockless = block.blockless;

				if (props.parentOptions && props.parentOptions.onDeleteClick) opt.onDeleteClick = props.parentOptions.onDeleteClick;

				blockItems.push({
					registerLinkedVariable: props.registerLinkedVariable,
					name: block.title,
					content: content,
					options: opt,
					container: props.data,
					linked: true,
				});
			}

			items = blockItems;
		}
	}

	const returnContent = items.map((val: IBlockItem, index: number) => {
		const key = (props.parentIndex ?? "^") + "_" + index;

		const sx: RecursiveCSS = {};

		if (val.options && val.options["hidden"]) {
			return null;
		}

		if (props.parentOptions && props.parentOptions.onDeleteClick) {
			if (val.options && val.options.type === "delete_button") {
				val.options.onDeleteClick = props.parentOptions.onDeleteClick;
			}
		}

		if (val.options && val.options["linked"]) {
			let value = null;
			const linkedKey = val.options.linked;

			if (typeof linkedKey === "string" && linkedKey.includes(".") && val.container) {
				const parts = linkedKey.split(".");
				let current: any = val.container;
				for (let i = 0; i < parts.length && current !== undefined && current !== null; i += 1) {
					current = current[parts[i]];
				}
				value = current;
			} else if (linkedKey !== undefined && linkedKey !== null && val.container) {
				if (typeof linkedKey === "string" || typeof linkedKey === "number") {
					value = (val.container as Record<string, unknown>)[String(linkedKey)];
				}
			}

			props.registerLinkedVariable(val.options.linked);
			if (Array.isArray(val.options.linked_value)) {
				if (!val.options.linked_value || !val.options.linked_value.includes(value)) {
					return null;
				}
			} else {
				if (value !== val.options.linked_value) {
					return null;
				}
			}
		}

		let innerObject = false;
		if (val.name && val.name.startsWith("$link_") && typeof val.container[val.content] === "object") {
			innerObject = true;
			val.name = val.content;
			val.content = val.container[val.content];
		}

		// Block with inner data
		if (typeof val.content === "object") {
			const decorator = val.options?.decorator as unknown as Record<string, any>;
			const hasDecoratedBlocks = decorator && decorator.blocks && Array.isArray(decorator.blocks);

			let contentBlock = null;
			/** RENDER OBJECTS */
			if (!Array.isArray(val.content)) {
				let posval = undefined;
				if (val.options && val.options.possible_values && Array.isArray(val.options.possible_values) && val.options.possible_values.length > 0)
					posval = val.options.possible_values;

				contentBlock = (
					<Block
						fieldErrors={props.fieldErrors}
						previewMode={props.previewMode}
						size={props.size}
						onComponentClick={props.onComponentClick}
						onFieldAction={props.onFieldAction}
						key={"b" + key}
						data={val.content}
						path={getItemPath(props.path, val)}
						isParentLinked={val.linked}
						parent={props.data}
						parentOptions={val.options}
						parentIndex={index + "_"}
						registerLinkedVariable={props.registerLinkedVariable}
						onFieldChange={props.onFieldChange}
					/>
				);

				if (posval) {
					contentBlock = (
						<>
							{contentBlock}
							<HBox>
								<Spacer grow />
								<Button
									variant="secondarySmall"
									onClick={(): void => {
										//todo: filter possible values by existing content

										S_SELECT_ITEMS_POPUP.invoke({
											title: msg("select") + createName(val.name),
											items: posval,
											multi: false,
											gridColumns: "max-content auto max-content min-content",
											searchable: false,

											onSelected: items => {
												if (items && items.length > 0) {
													const keys = Object.keys(val.content);
													for (const i of keys) {
														if (i in items[0]) {
															val.content[i] = items[0][i];
														}
													}
													// Replace object reference so Input's useEffect([data]) detects the change
													if (props.data && val.name in props.data) {
														props.data[val.name] = { ...val.content };
													}
													setState(state + 1);
													props.onFieldChange?.(val.name, normalizeFieldPath(getItemPath(props.path, val)));
												}
											},
											onClose: () => {},
										});
										return;
									}}>
									{createName(msg("choose_86a9b212") + val.name)}
								</Button>
							</HBox>
						</>
					);
				}
			} else {
				/** RENDER ARRAYS */
				contentBlock = <ArrayBlock key={"arr" + key} val={val} parentKey={key} index={index} blockProps={props} />;
			}

			if (hasDecoratedBlocks || innerObject) {
				let withoutTitle = !(val.options?.decorator as any)?.title;
				if ((val.options?.decorator as any)?.title === "") withoutTitle = false;
				if (withoutTitle) return contentBlock;
			}

			if (val.options?.blockless && (val.options?.decorator as any)?.title) {
				return (
					<VBox gap="4px" key={key}>
						<VBox sx={{ paddingBottom: "6px" }}>
							<Typo variant="medium" bold>
								{displayText((val.options?.decorator as any)?.title)}
							</Typo>
							{(val.options?.decorator as any)?.subtitle && (
								<Typo variant={props.size === "small" ? "caption" : "medium"}>{displayText((val.options?.decorator as any)?.subtitle)}</Typo>
							)}
						</VBox>

						{contentBlock}
					</VBox>
				);
			}

			const groupPath = normalizeFieldPath(getItemPath(props.path, val));
			const groupError = props.fieldErrors?.[groupPath]?.map(formatFieldErrorMessage).join(", ");
			if (groupError) contentBlock = <>
				<div id={getFieldErrorId(groupPath)} role="alert"><Typo variant="caption" sx={{ color: "#dc2626", fontSize: "13px", lineHeight: "17px", fontWeight: 600 }}>{groupError}</Typo></div>
				{contentBlock}
			</>;
			if (val.options?.blockless) return <Fragment key={key}>{contentBlock}</Fragment>;

			return (
				<Box
					sx={{
						//display: "contents",
						padding: props.variant === "reference" ? "16px" : "20px",
						backgroundColor: "#FFFFFF",
						borderRadius: props.variant === "reference" ? "10px" : "6px",
						boxShadow: props.variant === "reference" ? "none" : "0px 1px 2px rgba(0,0,0,.2)",
						border: props.variant === "reference" ? "1px solid #e6ddd8" : undefined,
						...(modern ? { padding: "20px", borderRadius: "12px", border: "1px solid #cdd5e2", boxShadow: "0 1px 2px #1018280d", backgroundColor: "#fff", minWidth: 0 } : {}),
						display: "grid",
						gap: modern ? "16px" : "10px",
						pointerEvents: val.options && val.options.disabled ? "none" : "all",
						...sx,
					}}
					key={key}>
					{
						/*SETUP BLOCK NAME*/
						!Array.isArray(props.data) && val.name && (
							<Box sx={{ marginBottom: "5px", display: "flex", flexDirection: "column", gap: "2px" }}>
								<Typo variant="medium" bold>
									{displayText((val.options?.decorator as any)?.title ?? createName(val.name))}
								</Typo>
								{(val.options?.decorator as any)?.subtitle && (
									<Typo variant={props.size === "small" ? "caption" : "medium"}>{displayText((val.options?.decorator as any)?.subtitle)}</Typo>
								)}
							</Box>
						)
					}
					{contentBlock}
				</Box>
			);
		}

		return (
			<InputBlock
				fieldErrors={props.fieldErrors}
				previewMode={props.previewMode}
				size={props.size}
				variant={props.variant}
				onComponentClick={props.onComponentClick}
				onFieldAction={props.onFieldAction}
				key={key}
				data={val}
				path={getItemPath(props.path, val)}
				onFieldChange={props.onFieldChange}
			/>
		);
	});

	if (Array.isArray(props.parent)) return modern && !props.parentOptions?.gridColumns && !props.parentOptions?.sx ? <Box sx={{ display: "grid", gap: "16px", minWidth: 0 }}>{returnContent}</Box> : returnContent;

	let gridBox: RecursiveCSS | null = null;
	if (props.parentOptions && props.parentOptions.gridColumns) {
		gridBox = {
			display: "grid",
			gridTemplateColumns: props.parentOptions.gridColumns, //"repeat(auto-fit, minmax(clamp(100px, 350px, 200px), 1fr))"
			alignItems: "start",
		};
	}
	if (props.parentOptions && props.parentOptions.sx && typeof props.parentOptions.sx === "object") {
		const parentSX = props.parentOptions.sx as RecursiveCSS;
		gridBox = {
			...gridBox,
			...parentSX,
		};
	}

	return (
		<Box
			sx={{
				display: "grid",
				gap: modern ? "20px" : "14px",
				...(modern ? { minWidth: 0 } : {}),
				...gridBox,
			}}>
			{returnContent}
		</Box>
	);
};

interface IArrayBlockProps {
	val: IBlockItem;
	parentKey: string;
	index: number;
	blockProps: IBlockProps;
}

const ARRAY_BLOCK_LIMIT = 10;

const matchesPossibleValue = (candidate: any, current: any, unique?: string | string[]): boolean => {
	if (candidate === null || typeof candidate !== "object") return candidate === current;
	if (!current || typeof current !== "object") return false;

	const uniqueKeys = unique ? (Array.isArray(unique) ? unique : [unique]) : [];
	const fallbackKey = ["id", "code", "value", "name"].find(key => key in candidate && key in current);
	const identityKeys = uniqueKeys.length > 0 ? uniqueKeys : fallbackKey ? [fallbackKey] : [];
	if (identityKeys.length > 0 && identityKeys.every(key => key in candidate && key in current)) {
		return identityKeys.every(key => candidate[key] === current[key]);
	}

	const sharedKeys = Object.keys(candidate).filter(key => !key.startsWith("$__") && key in current);
	return sharedKeys.length > 0 && sharedKeys.every(key => candidate[key] === current[key]);
};

const createPossibleValueItem = (item: any, template: any): any => {
	if (item.string) return item.name;
	const { selected, string, ...value } = item;
	if (!template || typeof template !== "object") return JSON.parse(JSON.stringify(value));
	const result = JSON.parse(JSON.stringify(template));
	for (const key of Object.keys(result)) {
		if (key in value) result[key] = value[key];
	}
	return result;
};

const ArrayBlock = ({ val, parentKey, index, blockProps }: IArrayBlockProps) => {
	useLocale();
	const theme = useTheme() as ITheme;
	const modern = useContext(FormAppearanceContext) === "modern";
	const [state, setState] = useState<number>(0);
	const [showAll, setShowAll] = useState<boolean>(false);

	const isItemViewable = (item: any) => {
		if (val.options?.hideEmpty && typeof item === "object") {
			if (isObjectEmpty(item)) {
				return false;
			}
		}
		if (blockProps.previewMode && isObjectEmpty(item)) {
			return false;
		}
		return true;
	};

	const getViewableItemsCount = () => {
		if (!val.content || !Array.isArray(val.content)) return 0;
		let count = 0;
		for (const item of val.content) {
			if (isItemViewable(item)) {
				count++;
			}
		}
		return count;
	};

	const totalItems = getViewableItemsCount();
	const isLimited = totalItems > ARRAY_BLOCK_LIMIT && !showAll;

	let asTileSX: RecursiveCSS = {};
	let asTileSXItem: RecursiveCSS = {};
	if (val.options?.astiles) {
		asTileSX = {
			display: "flex",
			flexWrap: "wrap",
		};

		asTileSXItem = {
			border: "1px solid rgba(0,0,0,.1)",
			borderRadius: "4px",
			boxSizing: "border-box",
			padding: "10px",
		};
	}

	return (
		<Box
			sx={{
				display: "grid",
				gap: modern || val.options?.asblocks ? "20px" : "10px",
				...asTileSX,
			}}>
			{val.content.map((arritem: any, arrindex: number) => {
				if (isLimited && arrindex >= ARRAY_BLOCK_LIMIT) return null;
				if (!isItemViewable(arritem)) return null;

				// Explicit layout metadata takes precedence over the modern default.
				const asBlocks = val.options?.asblocks ?? (
					modern && arritem !== null && typeof arritem === "object" && !Array.isArray(arritem) &&
					!val.options?.aslist && !val.options?.astiles && !val.options?.decorator && val.options?.type !== "country"
				);
				const innerKey = parentKey + "_" + arrindex;

				let gridSX: RecursiveCSS = {};

				if (val.options && val.options.gridColumns) {
					gridSX = {
						gridTemplateColumns: val.options.gridColumns,
						display: "grid",
						gap: "20px",
						alignItems: "center",
					};
				}

				if (val.options && val.options.sx && typeof val.options.sx === "object") {
					const valSX = val.options.sx as RecursiveCSS;
					gridSX = {
						...gridSX,
						...valSX,
					};
				}

				const declaredMin = Number(val.options?.min ?? (val.options?.optional ? 0 : 1));
				// Keep a schema row unless metadata can recreate an empty list.
				const minItems = Math.max(Array.isArray(val.options?.possible_values) || val.options?.item_template !== undefined ? 0 : 1, Number.isFinite(declaredMin) ? declaredMin : 1);
				const canRemove = !blockProps.previewMode && val.content.length > minItems;
				const rmbutton = !val.options?.ignore_delete_button && canRemove;

				let onRMClick = null;

				if (canRemove) {
					onRMClick = () => {
						if (val.content && Array.isArray(val.content)) {
							val.content.splice(arrindex, 1);
							setState(state + 1);
							blockProps.onFieldChange?.();
						}
					};
				}

				// Render the wrapper directly: a component declared here remounts inputs on every change.
				let renderContainer = (cProps: { children: React.ReactNode }) => {
					return <Fragment key={innerKey + "_A"}>{cProps.children}</Fragment>;
				};
				if (asBlocks) {
					renderContainer = (cProps: { children: React.ReactNode }) => {
						return (
							<VBox
								key={innerKey + "_A"}
								sx={{
									borderRadius: "8px",
									borderColor: theme.colors.divider,
									borderStyle: "solid",
									borderWidth: "1px",
									backgroundColor: modern ? "#fff" : "rgba(0,0,0,.03)",
									...(modern ? { borderColor: "#cdd5e2", borderRadius: "10px", boxShadow: "0 1px 2px #1018280d", minWidth: 0 } : {}),
								}}>
								<HBox
									sx={{
										height: "40px",
										alignItems: "center",
										backgroundColor: modern ? "#fff" : "rgba(0,0,0,.06)",
										...(modern ? { borderBottom: "1px solid #e1e6ef", borderRadius: "10px 10px 0 0", height: "auto", minHeight: "44px", gap: "12px" } : {}),
										padding: "10px 10px 10px 16px",
									}}>
									<Typo sx={modern ? { fontWeight: 600, color: "#344054", fontSize: "13px", overflowWrap: "anywhere" } : undefined}>{arrindex + 1 + ". " + (displayText(val.options?.itemtitle ?? createName(val.name)))}</Typo>

									{rmbutton && (modern ? (
										<RemoveItemButton type="button" aria-label={msg("remove_5948954e") + (displayText(val.options?.itemtitle ?? createName(val.name))) + " " + (arrindex + 1)} title={msg("removeItem")} onClick={() => onRMClick?.()}>
											<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
										</RemoveItemButton>
									) : (
										<Button
											variant="secondarySmall"
											sx={{
												...getReferenceFormButtonSx(blockProps.variant, modern),
												marginLeft: "auto",
												padding: "4px",
											}}
											onClick={() => {
												if (onRMClick) onRMClick();
											}}>
											<IconClose />
										</Button>
									))}
								</HBox>
								<Box sx={{ padding: "16px 16px 20px 16px" }}>{cProps.children}</Box>
							</VBox>
						);
					};
				}

				return renderContainer({ children: (
					<>
						<Box
							key={innerKey + "_A"}
							sx={{
								display: "grid",
								alignItems: "center",
								gap: "10px",
								gridTemplateColumns: (modern ? "minmax(0, 1fr) " : "1fr ") + (asBlocks ? "" : rmbutton ? "max-content" : ""),
								borderBottom:
									val.options?.borders && arrindex < val.content.length - 1 ? "1px solid rgba(0,0,0,.2)" : "0px solid rgba(0,0,0,.2)",
								paddingBottom: val.options?.borders && arrindex < val.content.length - 1 ? "25px" : "0",
								marginBottom: val.options?.borders && arrindex < val.content.length - 1 ? "15px" : "0",
								...asTileSXItem,
							}}>
							<Box sx={{ ...gridSX }}>
								{arritem !== null && typeof arritem !== "object" ? (
									<InputBlock
										fieldErrors={blockProps.fieldErrors}
										previewMode={blockProps.previewMode}
										size={blockProps.size}
										variant={blockProps.variant}
										onComponentClick={blockProps.onComponentClick}
										onFieldAction={blockProps.onFieldAction}
										data={{
											// Bind by position: empty or duplicate values still identify distinct rows.
											name: String(arrindex),
											content: arritem,
											container: val.content,
											options: {
												...val.options,
												label: null,
												type: val.options?.type ?? (Array.isArray(val.options?.values) ? "select" : "plain"),
											},
											registerLinkedVariable: blockProps.registerLinkedVariable,
										}}
										path={[...getItemPath(blockProps.path, val), String(arrindex)]}
										onFieldChange={blockProps.onFieldChange}
									/>
								) : (
									<Block
										fieldErrors={blockProps.fieldErrors}
										previewMode={blockProps.previewMode}
										size={blockProps.size}
										variant={blockProps.variant}
										registerLinkedVariable={blockProps.registerLinkedVariable}
										onComponentClick={blockProps.onComponentClick}
										onFieldAction={blockProps.onFieldAction}
										key={"b" + parentKey}
										data={arritem}
										path={[...getItemPath(blockProps.path, val), String(arrindex)]}
										isParentLinked={val.linked}
										parent={val.options && val.options.decorator ? blockProps.data : val.content}
										parentOptions={{ ...val.options, onDeleteClick: onRMClick as any }}
										parentIndex={index + innerKey + "_A"}
										onFieldChange={blockProps.onFieldChange}
									/>
								)}
							</Box>

							{rmbutton && !asBlocks ? (modern ? (
								<RemoveItemButton type="button" aria-label={msg("remove_5948954e") + createName(val.name) + " " + (arrindex + 1)} title={msg("removeItem")} onClick={() => onRMClick?.()}>
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
								</RemoveItemButton>
							) : (
								<Button
									variant="secondarySmall"
									sx={{
										padding: "4px",
									}}
									onClick={() => {
										if (onRMClick) onRMClick();
									}}>
									<IconClose />
								</Button>
							)) : null}
						</Box>
						{arrindex + 1 < val.content.length && val.options?.aslist && <HLine key={innerKey + "_B"} color="rgba(0,0,0,.1)" />}
					</>
				) });
			})}

			{totalItems > ARRAY_BLOCK_LIMIT && (
				<HBox>
					<Spacer grow />
					<Button variant="secondarySmall" sx={getReferenceFormButtonSx(blockProps.variant, modern)} onClick={() => setShowAll(!showAll)}>
						{showAll ? msg("showLess") : msg("templateShowAll_ca352b18", { v0: totalItems })}
					</Button>
				</HBox>
			)}

			{!val.options?.hide_add_button && !blockProps.previewMode && (
				<HBox>
					<Spacer grow />

					{val.options?.max && val.content.length >= parseInt(val.options.max, 10) && !Array.isArray(val.options?.possible_values) ? null : (
						<Button
							variant="secondarySmall"
							sx={getReferenceFormButtonSx(blockProps.variant, modern)}
							onClick={() => {
								const values = val.options?.possible_values;

								// ADD value from possible values
								if (val.options?.possible_values && Array.isArray(values)) {
									const currentItems = [...val.content];
									const unique = val.options?.unique as string | string[] | undefined;
									S_SELECT_ITEMS_POPUP.invoke({
										title: val.name === "fail_code" ? msg("failCodes") : createName(val.name),
										items: values,
										multi: true,
										manageSelection: true,
										maxSelected: val.options?.max ? parseInt(val.options.max, 10) : undefined,
										selectedItems: currentItems,
										comparator: (candidate, current) => matchesPossibleValue(candidate, current, unique),
										gridColumns: "max-content auto max-content min-content",
										searchable: false,

										onSelected: items => {
						if (items && Array.isArray(val.content)) {
							const template = currentItems.find(item => item && typeof item === "object");
							const nextItems = items.map(item =>
								currentItems.find(current => matchesPossibleValue(item.string ? item.name : item, current, unique)) ??
								createPossibleValueItem(item, template),
							);
							val.content.splice(0, val.content.length, ...nextItems);
							setState(state + 1);
							blockProps.onFieldChange?.(val.name, normalizeFieldPath(getItemPath(blockProps.path, val)));
						}
										},
										onClose: () => {},
									});
									return;
								}

								if (val.options?.type === "country") {
									S_SELECT_COUNTRIES_POPUP.invoke({
										onSuccess: countries => {
											for (let index = 0; index < countries.length; index++) {
												const country = countries[index];
												const newblock = JSON.parse(JSON.stringify(val.content[0]));
												for (const key in country) {
													if (key in newblock) newblock[key] = (country as any)[key];
												}
												let found = false;
												for (const j of val.content) {
													if (JSON.stringify(j).toLowerCase() === JSON.stringify(newblock).toLowerCase()) {
														found = true;
														break;
													}
												}
												if (!found) val.content.push(newblock);
											}
											setState(state + 1);
											blockProps.onFieldChange?.();
										},
									});
									return;
								} else {
									// add item

										if (val.content && (val.content.length || val.options?.item_template !== undefined)) {
										const newItemData = createEmptyFormItem(val.options?.item_template ?? val.content[val.content.length - 1], { type: val.options?.type });

										val.content.push(newItemData);
										setState(state + 1);
										blockProps.onFieldChange?.();
									}
								}
							}}>
							{val.options?.addbuttontitle ?? msg("add_8d89e4a8") + createName(val.name)}
						</Button>
					)}
				</HBox>
			)}
		</Box>
	);
};

const FieldActionButton = styled.button`
	display: grid; place-items: center; flex: 0 0 auto; min-width: 32px; padding: 0 8px;
	border: 1px solid #d0d5dd; border-radius: 4px; background: #fff; color: #475467;
	font: inherit; font-size: 13px; cursor: pointer;
	&:hover:not(:disabled) { background: #f8fafc; border-color: #98a2b3; }
	&:focus-visible { outline: 2px solid #167ca6; outline-offset: 2px; }
	&:disabled { cursor: default; opacity: .5; }
`;

const InputBlock = ({
	previewMode,
	size,
	variant,
	data,
	path,
	fieldErrors,
	onComponentClick,
	onFieldAction,
	onFieldChange,
}: {
	previewMode?: boolean;
	size: "small" | "medium" | "large";
	variant?: TFormParserVariant;
	data: IBlockItem;
	path: string[];
	fieldErrors?: TFieldErrors;
	onComponentClick?: (component: IBlockItem) => void;
	onFieldAction?: (event: FormFieldActionEvent) => void;
	onFieldChange?: (fieldName?: string, fieldPath?: string) => void;
}) => {
	useLocale();
	const [state, setState] = useState<number>(0);
	const mounted = useRef(true);
	useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
	const current = useRef({ data, disabled: false });
	current.current = { data, disabled: !!previewMode || !!data.options?.disabled };

	let name = data.name;
	let dataContent = data.content;
	if (name.startsWith("$link")) {
		name = data.content;
		dataContent = data.container[name];
	}
	const fieldPath = normalizeFieldPath(path);
	const fieldError = fieldErrors?.[fieldPath]?.map(formatFieldErrorMessage).join(", ");
	const inputID = "form_input_" + getFieldErrorId(fieldPath);
	const inputErrorSx = fieldError ? getInputFieldErrorSx(inputID) : undefined;
	const controlErrorSx = fieldError ? fieldErrorControlSx : undefined;

	let type = data.options?.type ?? "text";
	if (type !== "switchbox" && typeof dataContent === "boolean") type = "boolean";

	let switchboxLabels = { yes: msg("yes"), no: msg("no") };

	if (type === "switchbox" && data.options?.switchbox_labels && typeof data.options.switchbox_labels === "object") {
		switchboxLabels = {
			yes: displayText(data.options.switchbox_labels["yes"] ?? msg("yes")),
			no: displayText(data.options.switchbox_labels["no"] ?? msg("no")),
		};
	}

	let disabled = false;
	if (data.options?.disabled) disabled = true;
	if (previewMode) disabled = true;

	if (data.options?.hidden) return null;

	let units = "";
	if (data.options?.type === "%") units = " %";
	if (data.options?.type === "amount") units = " EUR";
	if (data.options?.units) units = data.options.units;

	let content = null;

	let label: string | undefined = createName(name);
	if (data.options?.label_uppercase) label = label.toUpperCase();
	if (data.options?.label) label = displayField(name, data.options.label);
	if (data.options?.label === null || data.options?.label === "") label = undefined;

	let sx = null;
	if (data.options?.sx && typeof data.options.sx === "object") sx = data.options.sx as RecursiveCSS;

	if (previewMode) {
		if (type === "select" || type === "country") type = "text";
	}

	switch (type) {
		case "select": {
			const values = displayOptions(data.options?.values ?? []);
			content = (
				<>
					<Select
						size={size}
						variant={variant}
						disabled={disabled}
						name={name}
						id={inputID}
						label={label}
						data={data.container}
						values={values as any[]}
						sx={controlErrorSx}
						onChange={() => onFieldChange?.(name, fieldPath)}
					/>
				</>
			);
			break;
		}

		case "file":
			content = (
				<FileSelect
					previewMode={previewMode}
					size={size}
					disabled={disabled}
					name={name}
					title={label}
					subtitle={data.options?.subtitle ? displayText(data.options.subtitle) : undefined}
					data={data.container}
					value={undefined}
				/>
			);
			break;

		case "link":
			content = (
				<Link
					size={size}
					disabled={disabled}
					name={name}
					title={label}
					subtitle={data.options?.subtitle ? displayText(data.options.subtitle) : undefined}
					data={data.container}
					icon={data.options?.icon}
					value={undefined}
				/>
			);
			break;

		case "radio": {
			const valuesRadio = displayOptions(data.options?.values ?? []);
			content = (
				<Radio
					size={size}
					disabled={disabled}
					name={name}
					label={label}
					data={data.container}
					values={valuesRadio as any[]}
					value={data.options?.value}
					onChange={() => onFieldChange?.(name, fieldPath)}
				/>
			);
			break;
		}

		case "delete_button":
			if (data.options?.onDeleteClick === undefined) return null;
			content = (
				<Button
					variant="secondarySmall"
					sx={{
						...getReferenceFormButtonSx(variant),
						padding: "4px",
						maxWidth: "25px",
						minWidth: "25px",
						maxHeight: "25px",
						minHeight: "25px",
						...(data.options?.sx as any),
					}}
					onClick={() => {
						if (data.options?.onDeleteClick && typeof data.options.onDeleteClick === "function") {
							(data.options as any).onDeleteClick();
						}
					}}>
					<IconClose />
				</Button>
			);
			break;

		case "country":
			content = (
				<Select
					size={size}
					variant={variant}
					disabled={disabled}
					name={"cc"}
					id={inputID}
					label={label}
					sx={controlErrorSx}
					// value={CountriesData.countries.find(c => c.alpha2 === data.container[name])}
					onChange={val => {
						data.container[name] = val.alpha2;
						onFieldChange?.(name, fieldPath);
					}}
					data={{ cc: data.container[name] }}
					values={CountriesData.countries.map(c => ({ ...c, name: countryName(c.alpha2, c.name) }))}
				/>
			);
			break;

		case "":
			break;

		case "plain":
			if (!data.container[name]) return null;
			content = (
				<Typo variant="medium" sx={sx ?? undefined}>
					{data.container[name]}{" "}
				</Typo>
			);
			break;

		case "button":
			if (!data.container[name]) return null;
			content = (
				<Button
					disabled={disabled}
					onClick={() => {
						if (onComponentClick) onComponentClick(data);
					}}>
					{typeof data.container[name] === "string" ? displayText(data.container[name]) : data.container[name]}
				</Button>
			);
			break;

		case "spacer":
			if (!data.container[name]) return null;
			content = <Spacer size={data.container[name]} />;
			break;

		case "date":
			// if (data.container[name] === "0" || data.container[name] === 0 || data.container[name] === undefined) {
			// 	const val = new Date();
			// 	data.container[name] = val.getFullYear() + "-" + String(val.getMonth() + 1).padStart(2, "0") + "-" + String(val.getDate()).padStart(2, "0");
			// }

			content = (
				<FormDateField
					container={data.container}
					name={name}
					id={inputID}
					label={label}
					size={size === "small" ? "small" : "medium"}
					disabled={disabled}
					invalid={!!fieldError}
					onChange={() => onFieldChange?.(name, fieldPath)}
				/>
			);
			break;

		case "href":
			if (!data.container[name]) return null;
			content = (
				<Typo
					variant="medium"
					onClick={() => {
						if (onComponentClick) onComponentClick(data);
					}}
					sx={{
						textDecoration: "underline",
						cursor: "pointer",
						textUnderlineOffset: "3px",
						textDecorationThickness: "1px",
						textDecorationSkipInk: "auto",
						pointerEvents: disabled ? "none" : "all",
					}}>
					{typeof data.container[name] === "string" ? displayText(data.container[name]) : data.container[name]}
				</Typo>
			);
			break;

		case "flag":
			if (!data.container[name]) return null;
			content = (
				<>
					<Box
						sx={{
							width: "35px",
							height: "25px",
							minWidth: "35px",
							minHeight: "25px",
							backgroundSize: "cover",
							boxShadow: "0px 1px 2px rgba(0,0,0,.2)",
							border: "2px solid #FFFFFF",
							borderRadius: "2px",
						}}
						style={{
							backgroundImage: "url(https://flagcdn.com/" + data.container[name]?.toLowerCase() + ".svg",
						}}></Box>
				</>
			);
			break;

		case "boolean":
			content = <Checkbox disabled={disabled} name={name} sx={{}} label={label} data={data.container} onChange={() => onFieldChange?.(name, fieldPath)} />;
			break;

		case "textarea":
			content = (
				<Input
					size={size}
					variant={variant}
					type="textarea"
					disabled={disabled}
					name={name}
					id={inputID}
					label={label}
					data={data.container}
					sx={{ ...inputErrorSx, minHeight: "100px" }}
					onChange={() => onFieldChange?.(name, fieldPath)}
				/>
			);
			break;

		case "sha256":
			content = (
				<HBox gap="10px">
					<Input
						disabled={disabled}
						key={state}
						name={name}
						id={inputID}
						size={size}
						variant={variant}
						label={label}
						data={data.container}
						sx={{ ...inputErrorSx, flexGrow: "1" }}
						onChange={() => onFieldChange?.(name, fieldPath)}
						iconRight={
							<Button
								variant="icon"
								sx={{ padding: "14px", margin: "2px", aspectRatio: "1 / 1" }}
								icon={<IconCopy size={"12"} />}
								onClick={async () => {
									if (data.container[name]) {
										if ("clipboard" in navigator) {
											await navigator.clipboard.writeText(data.container[name]);
										} else {
											document.execCommand("copy", true, data.container[name]);
										}
										notify.copied();
									}
								}}
							/>
						}
					/>
					<Button
						disabled={disabled}
						sx={{ ...getReferenceFormButtonSx(variant), alignSelf: "end", height: variant === "reference" ? "30px" : "46px" }}
						variant="secondarySmall"
						onClick={() => {
							const process = () => {
								data.container[name] = "";
								setState(state + 1);
								onFieldChange?.(name, fieldPath);
							};
							if (data.container[name]) {
								S_CONFIRM.invoke({
									message: msg("confirmTokenClear"),
									onResult: (val: "confirm" | "reject") => {
										if (val === "confirm") {
											process();
										}
									},
								});
							} else {
								process();
							}
						}}
						title={msg("clear")}
					/>
					<Button
						sx={{ ...getReferenceFormButtonSx(variant), alignSelf: "end", height: variant === "reference" ? "30px" : "46px" }}
						variant="secondarySmall"
						disabled={disabled}
						onClick={async () => {
							const process = async () => {
								const newValue = await randomSHA256();
								data.container[name] = newValue;
								setState(state + 1);
								onFieldChange?.(name, fieldPath);
							};
							if (data.container[name]) {
								S_CONFIRM.invoke({
									message: msg("confirmTokenChange"),
									onResult: (val: "confirm" | "reject") => {
										if (val === "confirm") {
											process();
										}
									},
								});
							} else {
								process();
							}
						}}
						title={msg("generate")}
					/>
				</HBox>
			);
			break;

		//!TODO: CASE currency, phone, country

		//!TODO: validator type;
		default: {
			let customName = undefined;
			let customData = undefined;
			let useCustomObject = false;

			if (
				previewMode &&
				data.options?.type === "select" &&
				data.options?.values &&
				Array.isArray(data.options.values) &&
				data.options.values.length > 0
			) {
				if (typeof data.options.values[0] === "object") {
					const selectedValue = data.options.values.find((v: any) => v.value === data.container[name]);

					if (selectedValue && selectedValue.label && selectedValue.value) {
						useCustomObject = true;
						customName = "label";
						customData = selectedValue;
					}
				}
			}

			content = (
				<Input
					id={inputID}
					disabled={disabled}
					size={size}
					variant={variant}
					name={useCustomObject && customName ? customName : name}
					label={label}
					units={units}
					formatAmount={type === "amount"}
					asPercent={type === "%"}
					data={useCustomObject ? customData : data.container}
					type={type === "switchbox" ? "switchbox" : type === "password" ? "password" : "text"}
					switchboxLabels={switchboxLabels}
					refvalue={data?.options?.refvalue}
					validator={data?.options?.validator as any}
					sx={inputErrorSx}
					onChange={() => onFieldChange?.(name, fieldPath)}
				/>
			);
		}
	}

	const actions = parseFieldActions(data.options?.actions);
	for (const action of actions) {
		if (action.linked) data.registerLinkedVariable(action.linked.split(".").at(-1)!);
	}
	const visibleActions = onFieldAction && !previewMode ? actions.filter(action => fieldActionVisible(action, data.container)) : [];
	if (visibleActions.length && content) content = <HBox gap="6px" sx={{ alignItems: "end", minWidth: 0 }}>
		{/* Data-bound controls reread the external value after an action; keep the trigger mounted for focus restoration. */}
		<Box key={state} sx={{ flex: "1 1 auto", minWidth: 0 }}>{content}</Box>
		{visibleActions.map(action => <FieldActionButton key={action.id} type="button" title={displayText(action.label)} aria-label={displayText(action.label)}
			aria-haspopup={action.popup ? "dialog" : undefined} disabled={disabled || action.disabled}
			style={{ height: variant === "reference" ? "30px" : size === "small" ? "36px" : size === "large" ? "58px" : "46px" }}
			onClick={() => {
				if (disabled || action.disabled || !fieldActionVisible(action, data.container)) return;
				const container = data.container;
				const previous = container[name];
				const dependency = fieldActionDependency(action, container);
				onFieldAction?.({ action, name, path: fieldPath, value: previous, data: container, setValue: value => {
					if (!mounted.current || current.current.disabled || current.current.data.container !== container ||
						container[name] !== previous || fieldActionDependency(action, container) !== dependency || !fieldActionVisible(action, container)) return false;
					container[name] = value;
					setState(current => current + 1);
					onFieldChange?.(name, fieldPath);
					return true;
				} });
			}}>{displayText(action.text ?? action.label)}</FieldActionButton>)}
	</HBox>;

	if (content && typeof data.options?.description === "string" && data.options.description) {
		content = <VBox gap="5px">
			{content}
			<Typo variant="caption" sx={{ opacity: 0.75, whiteSpace: "pre-wrap" }}>{displayText(data.options.description)}</Typo>
		</VBox>;
	}

	if (!fieldError) return content;

	return (
		<VBox id={getFieldErrorId(fieldPath)} gap="5px">
			{content}
			<Typo variant="caption" sx={{ color: "#dc2626", fontSize: "13px", lineHeight: "17px", fontWeight: 600 }}>
				{fieldError}
			</Typo>
		</VBox>
	);
};

// utils
export const createName = (str: string): string => {
	if (!str) return "";
	const field = str;
	str = str.replaceAll("pluss_pluss", "++");
	str = str.replaceAll("e_mail", "e-mail");
	str = str.replaceAll("_", " ").trim();

	const parts = str.split(" ");

	if (str.toLowerCase() === "three ds") return "3DS";

	for (let i = 0; i < parts.length; i++) {
		let lstr = parts[i].toLowerCase();
		if (lstr === "firstname") lstr = "First Name";
		if (lstr === "lastname") lstr = "Last Name";
		if (lstr === "middlename") lstr = "Middle Name";
		if (lstr === "phonenumber") lstr = "Phone Number";
		if (lstr === "zipcode") lstr = "Zip Code";
		if (lstr === "postalcode") lstr = "Postal Code";
		if (lstr === "cvv") lstr = "CVV";
		if (lstr === "ssn") lstr = "SSN";
		if (lstr === "dob") lstr = "Date of Birth";
		if (lstr === "email") lstr = "e-mail";
		if (lstr === "id") lstr = "ID";
		if (lstr === "mcc") lstr = "MCC";
		if (lstr === "url") lstr = "URL";
		if (lstr === "ip") lstr = "IP";
		if (lstr === "3ds") lstr = "3DS";
		if (lstr === "pci") lstr = "PCI";
		if (lstr === "dss") lstr = "DSS";
		parts[i] = lstr;
	}

	str = parts.join(" ");

	return displayField(field, str.charAt(0).toUpperCase() + str.slice(1));
};

const isObjectEmpty = (obj: any): boolean => {
	let fields = 0;
	let emptyFields = 0;
	if (typeof obj !== "object" || obj === null || obj === "") return true;
	const keys = Object.keys(obj);

	for (const i in keys) {
		if (keys[i].startsWith("$__")) {
			continue;
		}

		fields++;
		if (typeof obj[keys[i]] === "object") {
			if (isObjectEmpty(obj[keys[i]])) {
				emptyFields++;
			}
			continue;
		}

		if (obj["$__" + keys[i] + "_hidden"] || obj["$__" + keys[i] + "_type"] === "delete_button" /*|| obj["$__" + keys[i] + "_type"] === "plain"*/) {
			emptyFields++;
			continue;
		}

		if (obj[keys[i]] === "" || obj[keys[i]] === null || obj[keys[i]] === undefined) emptyFields++;

		if (emptyFields > 0 && (obj[keys[i]] === 0 || obj[keys[i]] === false)) {
			emptyFields++;
		}
	}

	return emptyFields === fields;
};
